import {
  aggregateFlowCells,
  cellValueAt,
  clampBubbleCenter,
  selectVolumeDots,
  STRIKE_WINDOW_PTS,
  volumeDotAmbiguousCanvasStyle,
  volumeDotSegmentGeometry,
} from '../../Utils/strikeFlow';

import { bucketNormalizedValue } from '../../Utils/scales';
import { bookmapHeatCssColor } from '../../Utils/bookmapHeatPalette';

const HEAT_ROW_FILL_RATIO = 0.42;
const HEAT_ROW_MIN_HEIGHT = 3;
const HEAT_ROW_MAX_HEIGHT = 10;

function toChartTime(ms) {
  return Math.floor(ms / 60_000) * 60;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// -----------------------------------------------------------------------------
// Exact Flowmap bubble feed.
//
// This publishes the FINAL bubble objects produced by _bubbleGeometry().
//
// Consumers therefore receive the exact bubbles Flowmap is drawing:
//
// - same timestamp
// - same +/-80-point strike window
// - same volume threshold decision
// - same gross volume
// - same Net Delta
// - same bullish/bearish Delta composition
// - same radius
// - same climax state
// - same recovery-gap state
// - same visible-range filtering
//
// GexPhaseDeltaFlow can subscribe to this rather than independently rebuilding
// the Flowmap bubble calculations.
// -----------------------------------------------------------------------------

let publishedBookmapBubbles = [];
let publishedBookmapBubbleOwner = null;

const bookmapBubbleListeners = new Set();

function publishBookmapBubbles(owner, bubbles) {
  publishedBookmapBubbleOwner = owner;
  publishedBookmapBubbles = Array.isArray(bubbles) ? bubbles : [];

  for (const listener of bookmapBubbleListeners) {
    listener();
  }
}

function clearPublishedBookmapBubbles(owner) {
  if (publishedBookmapBubbleOwner !== owner) {
    return;
  }

  publishedBookmapBubbleOwner = null;
  publishedBookmapBubbles = [];

  for (const listener of bookmapBubbleListeners) {
    listener();
  }
}

export function subscribeBookmapBubbles(listener) {
  bookmapBubbleListeners.add(listener);

  return () => {
    bookmapBubbleListeners.delete(listener);
  };
}

export function getBookmapBubblesSnapshot() {
  return publishedBookmapBubbles;
}

// -----------------------------------------------------------------------------
// Pre-threshold Flowmap bubble candidate feed.
//
// These are the exact +/-80-point aggregates used by Flowmap BEFORE
// selectVolumeDots() applies the Flowmap's own volume threshold and radius
// scaling. GexPhaseDeltaFlow subscribes here so it can apply an independent
// threshold without rebuilding the underlying Delta/volume data.
// -----------------------------------------------------------------------------

let publishedBookmapBubbleCandidates = [];
let publishedBookmapBubbleCandidateOwner = null;

const bookmapBubbleCandidateListeners = new Set();

function publishBookmapBubbleCandidates(owner, candidates) {
  publishedBookmapBubbleCandidateOwner = owner;
  publishedBookmapBubbleCandidates = Array.isArray(candidates) ? candidates : [];

  for (const listener of bookmapBubbleCandidateListeners) {
    listener();
  }
}

function clearPublishedBookmapBubbleCandidates(owner) {
  if (publishedBookmapBubbleCandidateOwner !== owner) {
    return;
  }

  publishedBookmapBubbleCandidateOwner = null;
  publishedBookmapBubbleCandidates = [];

  for (const listener of bookmapBubbleCandidateListeners) {
    listener();
  }
}

export function subscribeBookmapBubbleCandidates(listener) {
  bookmapBubbleCandidateListeners.add(listener);

  return () => {
    bookmapBubbleCandidateListeners.delete(listener);
  };
}

export function getBookmapBubbleCandidatesSnapshot() {
  return publishedBookmapBubbleCandidates;
}

export function priceAtChartTime(line, time) {
  if (!line?.length || !Number.isFinite(time)) return null;

  let previous = null;

  for (const point of line) {
    const value =
      typeof point?.value === 'number'
        ? point.value
        : NaN;

    if (
      !Number.isFinite(point?.time)
      || !Number.isFinite(value)
    ) {
      continue;
    }

    if (point.time === time) {
      return value;
    }

    if (point.time > time) {
      if (!previous) {
        return null;
      }

      const span = point.time - previous.time;

      return span > 0
        ? previous.value
          + ((time - previous.time) / span)
          * (value - previous.value)
        : previous.value;
    }

    previous = {
      time: point.time,
      value,
    };
  }

  return previous?.value ?? null;
}

export function bubbleCanvasEncoding(bubble) {
  if (bubble.deltaEstimated) {
    return {
      kind: 'estimated',
      ambiguousStyle: volumeDotAmbiguousCanvasStyle(
        bubble.netDelta,
      ),
    };
  }

  if ((bubble.buy || 0) + (bubble.sell || 0) === 0) {
    return {
      kind: 'neutral',
    };
  }

  const segments = bubble.directionalSegments?.length
    ? bubble.directionalSegments
    : [
        {
          side: 'aggregate',
          total: bubble.grossDelta || 1,
          tone:
            bubble.share >= 0.5
              ? 'bullish'
              : 'bearish',
          counterShare: Math.min(
            bubble.share,
            1 - bubble.share,
          ),
        },
      ];

  return {
    kind: 'directional',
    baseTone:
      segments[0]?.tone
      || 'neutral',
    geometry:
      volumeDotSegmentGeometry(segments),
  };
}

export function bubbleOutlineEncoding(bubble) {
  return {
    primaryStroke: bubble.recoveryGap
      ? '#ff8a1f'
      : bubble.deltaEstimated
        ? 'rgba(203,213,225,0.76)'
        : 'rgb(0,0,0)',

    recoveryGlowStroke:
      bubble.recoveryGap
        ? 'rgba(255,138,31,0.22)'
        : null,

    climaxStroke:
      bubble.climax
        ? '#ffffff'
        : null,
  };
}

class PaneView {
  constructor(zOrder, draw) {
    this._zOrder = zOrder;
    this._draw = draw;
  }

  zOrder() {
    return this._zOrder;
  }

  renderer() {
    return {
      draw: this._draw,
    };
  }
}

export class BookmapMainPrimitive {
  constructor() {
    this._data = {};
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._bubbleCandidates = [];
    this._bubbles = [];

    this._views = [
      new PaneView(
        'bottom',
        (target) => this._drawHeat(target),
      ),
      new PaneView(
        'top',
        (target) => this._drawBubbles(target),
      ),
    ];
  }

  attached({
    chart,
    series,
    requestUpdate,
  }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    clearPublishedBookmapBubbleCandidates(this);
    clearPublishedBookmapBubbles(this);

    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._bubbleCandidates = [];
    this._bubbles = [];
  }

  setData(data) {
    this._data = data || {};

    this.updateAllViews();

    this._requestUpdate?.();
  }

  updateAllViews() {
    const range =
      this._chart
        ?.timeScale()
        .getVisibleLogicalRange?.();

    // Build the underlying Flowmap aggregates once. These candidates are
    // pre-threshold and are shared with GexPhaseDeltaFlow.
    this._bubbleCandidates =
      this._buildBubbleCandidates(
        range?.from,
        range?.to,
      );

    // Flowmap keeps its own existing threshold and sizing behavior.
    this._bubbles =
      this._bubbleGeometryFromCandidates(
        this._bubbleCandidates,
      );

    publishBookmapBubbleCandidates(
      this,
      this._bubbleCandidates,
    );

    // Preserve the existing final-bubble feed for any current consumers.
    publishBookmapBubbles(
      this,
      this._bubbles,
    );
  }

  paneViews() {
    return this._views;
  }

  bubbleForId(id) {
    return (
      this._bubbles.find(
        (bubble) => bubble.id === id,
      )
      || null
    );
  }

  bubbleAtChartTime(time) {
    const chartTime = Number(time);

    if (!Number.isFinite(chartTime)) {
      return null;
    }

    return (
      this._bubbles.find(
        (bubble) =>
          toChartTime(bubble.t)
          === chartTime,
      )
      || null
    );
  }

  hitTest(x, y) {
    for (
      let index = this._bubbles.length - 1;
      index >= 0;
      index -= 1
    ) {
      const bubble = this._bubbles[index];

      if (
        Math.hypot(
          x - bubble.cx,
          y - bubble.cy,
        )
        <= bubble.r + 3
      ) {
        return {
          distance: 0,
          hitTestPriority: 2,
          itemType: 'marker',
          externalId: bubble.id,
          zOrder: 'top',
          cursorStyle: 'pointer',
        };
      }
    }

    return null;
  }

  _buildBubbleCandidates(
    startTimePoint,
    endTimePoint,
  ) {
    const {
      dots,
      flowSnaps = [],
    } = this._data;

    if (
      !dots
      || !this._chart
      || !this._series
    ) {
      return [];
    }

    const timeScale =
      this._chart.timeScale();

    // `timeScale.width()` reports 0 in the bundled build while the pane is a
    // real width. Treat a non-positive width as "not measurable yet" rather
    // than as an empty pane.
    const paneWidth =
      this._chart.paneSize?.()?.width
      || timeScale.width?.();

    const rightEdge =
      Number.isFinite(paneWidth)
      && paneWidth > 0
        ? paneWidth
        : Infinity;

    return flowSnaps
      .map((snap) => {
        const time =
          toChartTime(snap.t);

        const logical =
          timeScale
            .timeToIndex?.(
              time,
              false,
            );

        const cx =
          timeScale
            .timeToCoordinate(time);

        if (!Number.isFinite(cx)) {
          return null;
        }

        if (
          cx < 0
          || cx > rightEdge
        ) {
          return null;
        }

        if (
          Number.isFinite(startTimePoint)
          && Number.isFinite(endTimePoint)
          && Number.isFinite(logical)
          && (
            logical < startTimePoint
            || logical > endTimePoint
          )
        ) {
          return null;
        }

        const flow =
          aggregateFlowCells(
            snap.byStrike,
            snap.spot,
            STRIKE_WINDOW_PTS,
          );

        return {
          ...flow,
          t: snap.t,
          cx,
          recoveryGap:
            snap.recoveryGap,
          gapMinutes:
            snap.gapMinutes,
        };
      })
      .filter(
        (flow) =>
          flow
          && Number.isFinite(
            flow.avgStrike,
          )
          && flow.dVol > 0,
      );
  }

  _bubbleGeometryFromCandidates(candidates) {
    const {
      minVolume = 0,
      plotTop = 0,
      plotBottom: configuredPlotBottom,
    } = this._data;
    const paneHeight = this._chart?.paneSize?.()?.height;
    const plotBottom = Number.isFinite(configuredPlotBottom)
      ? configuredPlotBottom
      : paneHeight;

    return selectVolumeDots(
      candidates,
      minVolume,
    )
      .map((bubble) => {
        const rawCy =
          this._series
            .priceToCoordinate(
              bubble.avgStrike,
            );
        const cy = Number.isFinite(plotBottom)
          ? clampBubbleCenter(rawCy, bubble.r, plotTop, plotBottom)
          : rawCy;

        return {
          ...bubble,

          id:
            `bookmap-bubble:${bubble.t}`,

          strike:
            bubble.avgStrike,

          cy,
        };
      })
      .filter(
        (bubble) =>
          Number.isFinite(bubble.cx)
          && Number.isFinite(bubble.cy),
      );
  }

  _bubbleGeometry(
    startTimePoint,
    endTimePoint,
  ) {
    return this._bubbleGeometryFromCandidates(
      this._buildBubbleCandidates(
        startTimePoint,
        endTimePoint,
      ),
    );
  }

  _drawHeat(target) {
    const {
      snaps = [],
      rows = [],
      layer = 'gamma',
      palette = 'bookmap',
    } = this._data;

    if (
      !this._chart
      || !this._series
      || snaps.length < 2
      || rows.length < 2
    ) {
      return;
    }

    const xs = snaps
      .map((snap) => ({
        snap,

        x:
          this._chart
            .timeScale()
            .timeToCoordinate(
              toChartTime(
                snap.t,
              ),
            ),
      }))
      .filter(
        (entry) =>
          Number.isFinite(
            entry.x,
          ),
      );

    const ys = rows
      .slice()
      .reverse()
      .map((strike) => ({
        strike,

        y:
          this._series
            .priceToCoordinate(
              strike,
            ),
      }))
      .filter(
        (entry) =>
          Number.isFinite(
            entry.y,
          ),
      );

    if (
      xs.length < 2
      || ys.length < 2
    ) {
      return;
    }

    target.useMediaCoordinateSpace(
      ({
        context: ctx,
        mediaSize,
      }) => {
        for (
          let xi = 0;
          xi < xs.length;
          xi += 1
        ) {
          const x =
            xs[xi];

          const prev =
            xs[xi - 1];

          const next =
            xs[xi + 1];

          const minuteAgo =
            this._chart
              .timeScale()
              .timeToCoordinate(
                toChartTime(
                  x.snap.t,
                ) - 60,
              );

          const minuteAhead =
            this._chart
              .timeScale()
              .timeToCoordinate(
                toChartTime(
                  x.snap.t,
                ) + 60,
              );

          const neighborLeft =
            prev
              ? (
                prev.x
                + x.x
              ) / 2
              : next
                ? x.x
                  - (
                    next.x
                    - x.x
                  ) / 2
                : x.x - 4;

          const neighborRight =
            next
              ? (
                x.x
                + next.x
              ) / 2
              : prev
                ? x.x
                  + (
                    x.x
                    - prev.x
                  ) / 2
                : x.x + 4;

          const rawLeft =
            clamp(
              Number.isFinite(
                minuteAgo,
              )
                ? Math.max(
                    neighborLeft,
                    (
                      x.x
                      + minuteAgo
                    ) / 2,
                  )
                : neighborLeft,

              0,
              mediaSize.width,
            );

          const rawRight =
            clamp(
              Number.isFinite(
                minuteAhead,
              )
                ? Math.min(
                  neighborRight,
                  (
                    x.x
                    + minuteAhead
                  ) / 2,
                )
                : neighborRight,
              0,
              mediaSize.width,
            );

          /*
           * Snap both boundaries to the nearest pixel.
           *
           * Do NOT use floor(left) / ceil(right).
           * That causes adjacent transparent cells to overlap
           * by roughly one pixel and creates visible vertical
           * intensity stripes.
           */
          const left =
            Math.max(
              0,
              Math.round(
                rawLeft,
              ),
            );

          const right =
            Math.min(
              mediaSize.width,
              Math.round(
                rawRight,
              ),
            );

          if (right <= left) {
            continue;
          }

          for (
            let yi = 0;
            yi < ys.length;
            yi += 1
          ) {
            const row =
              ys[yi];

            const prevRow =
              ys[yi - 1];

            const nextRow =
              ys[yi + 1];

            const prevGap =
              prevRow
                ? Math.abs(
                    row.y
                    - prevRow.y,
                  )
                : Infinity;

            const nextGap =
              nextRow
                ? Math.abs(
                    nextRow.y
                    - row.y,
                  )
                : Infinity;

            const nearestGap =
              Math.min(
                prevGap,
                nextGap,
              );

            const rowSpacing =
              Number.isFinite(
                nearestGap,
              )
                ? nearestGap
                : 8;

            const bandHeight =
              clamp(
                rowSpacing
                  * HEAT_ROW_FILL_RATIO,

                HEAT_ROW_MIN_HEIGHT,
                HEAT_ROW_MAX_HEIGHT,
              );

            const top =
              clamp(
                row.y
                  - bandHeight / 2,

                0,
                mediaSize.height,
              );

            const bottom =
              clamp(
                row.y
                  + bandHeight / 2,

                0,
                mediaSize.height,
              );

            const value =
              cellValueAt(
                x.snap,
                row.strike,
                layer,
              );

            if (
              !value
              || bottom <= top
            ) {
              continue;
            }

            ctx.fillStyle =
              bookmapHeatCssColor(
                bucketNormalizedValue(
                  value,
                  layer,
                ),

                palette,
                layer,
              );

            ctx.fillRect(
              left,
              top,
              right - left,
              bottom - top,
            );
          }
        }
      },
    );
  }

  _drawBubbles(target) {
    const bubbles =
      this._bubbles;

    if (!bubbles.length) {
      return;
    }

    target.useMediaCoordinateSpace(
      ({
        context: ctx,
      }) => {
        for (const bubble of bubbles) {
          const encoding =
            bubbleCanvasEncoding(
              bubble,
            );

          const outline =
            bubbleOutlineEncoding(
              bubble,
            );

          ctx.save();

          if (
            encoding.kind
            === 'estimated'
          ) {
            ctx.beginPath();

            ctx.arc(
              bubble.cx,
              bubble.cy,
              bubble.r,
              0,
              Math.PI * 2,
            );

            ctx.fillStyle =
              encoding
                .ambiguousStyle
                .fillStyle;

            ctx.fill();

            ctx.save();

            ctx.beginPath();

            ctx.arc(
              bubble.cx,
              bubble.cy,
              bubble.r,
              0,
              Math.PI * 2,
            );

            ctx.clip();

            ctx.strokeStyle =
              encoding
                .ambiguousStyle
                .hatchStyle;

            ctx.lineWidth =
              encoding
                .ambiguousStyle
                .hatchLineWidth;

            for (
              let offset =
                -bubble.r * 2;

              offset
              <= bubble.r * 2;

              offset +=
                encoding
                  .ambiguousStyle
                  .hatchGap
            ) {
              ctx.beginPath();

              ctx.moveTo(
                bubble.cx
                  - bubble.r
                  + offset,

                bubble.cy
                  - bubble.r,
              );

              ctx.lineTo(
                bubble.cx
                  + bubble.r
                  + offset,

                bubble.cy
                  + bubble.r,
              );

              ctx.stroke();
            }

            ctx.restore();
          } else {
            ctx.beginPath();

            ctx.arc(
              bubble.cx,
              bubble.cy,
              bubble.r,
              0,
              Math.PI * 2,
            );

            ctx.fillStyle =
              encoding.kind === 'neutral'
                ? 'rgba(140,148,160,0.8)'
                : encoding.baseTone === 'bullish'
                  ? 'rgb(0,191,58)'
                  : 'rgb(208,0,0)';

            ctx.fill();

            for (
              const segment
              of encoding.geometry || []
            ) {
              if (
                segment.counterSweepDeg
                <= 0
              ) {
                continue;
              }

              const start =
                (
                  segment.counterStartDeg
                  * Math.PI
                ) / 180;

              const end =
                (
                  (
                    segment.counterStartDeg
                    + segment.counterSweepDeg
                  )
                  * Math.PI
                ) / 180;

              ctx.beginPath();

              ctx.moveTo(
                bubble.cx,
                bubble.cy,
              );

              ctx.arc(
                bubble.cx,
                bubble.cy,
                Math.max(
                  1.5,
                  bubble.r * 0.78,
                ),
                start,
                end,
              );

              ctx.closePath();

              ctx.fillStyle =
                'rgba(3,7,18,0.58)';

              ctx.fill();
            }
          }

          ctx.beginPath();

          ctx.arc(
            bubble.cx,
            bubble.cy,
            bubble.r,
            0,
            Math.PI * 2,
          );

          ctx.strokeStyle =
            'rgba(5,7,10,0.7)';

          ctx.lineWidth = 1.1;

          ctx.setLineDash([]);

          ctx.stroke();

          ctx.beginPath();

          ctx.arc(
            bubble.cx,
            bubble.cy,
            Math.max(
              0,
              bubble.r - 0.4,
            ),
            0,
            Math.PI * 2,
          );

          ctx.strokeStyle =
            outline.primaryStroke;

          ctx.lineWidth = 1.7;

          ctx.stroke();

          if (
            outline.recoveryGlowStroke
          ) {
            ctx.beginPath();

            ctx.arc(
              bubble.cx,
              bubble.cy,
              bubble.r + 2.5,
              0,
              Math.PI * 2,
            );

            ctx.strokeStyle =
              outline
                .recoveryGlowStroke;

            ctx.lineWidth = 3;

            ctx.stroke();
          }

          if (
            outline.climaxStroke
          ) {
            ctx.beginPath();

            ctx.arc(
              bubble.cx,
              bubble.cy,
              bubble.r + 3.5,
              0,
              Math.PI * 2,
            );

            ctx.strokeStyle =
              outline
                .climaxStroke;

            ctx.lineWidth = 2;

            ctx.stroke();
          }

          ctx.restore();
        }
      },
    );
  }
}





// import {
//   aggregateFlowCells,
//   cellValueAt,
//   selectVolumeDots,
//   STRIKE_WINDOW_PTS,
//   volumeDotAmbiguousCanvasStyle,
//   volumeDotSegmentGeometry,
// } from '../../Utils/strikeFlow';
//
// import { bucketNormalizedValue } from '../../Utils/scales';
// import { bookmapHeatCssColor } from '../../Utils/bookmapHeatPalette';
//
// const HEAT_ROW_FILL_RATIO = 0.42;
// const HEAT_ROW_MIN_HEIGHT = 3;
// const HEAT_ROW_MAX_HEIGHT = 10;
//
// function toChartTime(ms) {
//   return Math.floor(ms / 60_000) * 60;
// }
//
// function clamp(value, min, max) {
//   return Math.max(min, Math.min(max, value));
// }
//
// export function priceAtChartTime(line, time) {
//   if (!line?.length || !Number.isFinite(time)) return null;
//   let previous = null;
//   for (const point of line) {
//     const value = typeof point?.value === 'number' ? point.value : NaN;
//     if (!Number.isFinite(point?.time) || !Number.isFinite(value)) continue;
//     if (point.time === time) return value;
//     if (point.time > time) {
//       if (!previous) return null;
//       const span = point.time - previous.time;
//       return span > 0
//         ? previous.value + ((time - previous.time) / span) * (value - previous.value)
//         : previous.value;
//     }
//     previous = { time: point.time, value };
//   }
//   return previous?.value ?? null;
// }
//
// export function bubbleCanvasEncoding(bubble) {
//   if (bubble.deltaEstimated) {
//     return {
//       kind: 'estimated',
//       ambiguousStyle: volumeDotAmbiguousCanvasStyle(bubble.netDelta),
//     };
//   }
//   if ((bubble.buy || 0) + (bubble.sell || 0) === 0) {
//     return { kind: 'neutral' };
//   }
//   const segments = bubble.directionalSegments?.length
//     ? bubble.directionalSegments
//     : [{
//         side: 'aggregate',
//         total: bubble.grossDelta || 1,
//         tone: bubble.share >= 0.5 ? 'bullish' : 'bearish',
//         counterShare: Math.min(bubble.share, 1 - bubble.share),
//       }];
//   return {
//     kind: 'directional',
//     baseTone: segments[0]?.tone || 'neutral',
//     geometry: volumeDotSegmentGeometry(segments),
//   };
// }
//
// export function bubbleOutlineEncoding(bubble) {
//   return {
//     primaryStroke: bubble.recoveryGap
//       ? '#ff8a1f'
//       : bubble.deltaEstimated
//         ? 'rgba(203,213,225,0.76)'
//         : 'rgb(0,0,0)',
//     recoveryGlowStroke: bubble.recoveryGap ? 'rgba(255,138,31,0.22)' : null,
//     climaxStroke: bubble.climax ? '#ffffff' : null,
//   };
// }
//
// class PaneView {
//   constructor(zOrder, draw) {
//     this._zOrder = zOrder;
//     this._draw = draw;
//   }
//
//   zOrder() {
//     return this._zOrder;
//   }
//
//   renderer() {
//     return { draw: this._draw };
//   }
// }
//
// export class BookmapMainPrimitive {
//   constructor() {
//     this._data = {};
//     this._chart = null;
//     this._series = null;
//     this._requestUpdate = null;
//     this._bubbles = [];
//     this._views = [
//       new PaneView('bottom', (target) => this._drawHeat(target)),
//       new PaneView('top', (target) => this._drawBubbles(target)),
//     ];
//   }
//
//   attached({ chart, series, requestUpdate }) {
//     this._chart = chart;
//     this._series = series;
//     this._requestUpdate = requestUpdate;
//   }
//
//   detached() {
//     this._chart = null;
//     this._series = null;
//     this._requestUpdate = null;
//     this._bubbles = [];
//   }
//
//   setData(data) {
//     this._data = data || {};
//     this.updateAllViews();
//     this._requestUpdate?.();
//   }
//
//   updateAllViews() {
//     const range = this._chart?.timeScale().getVisibleLogicalRange?.();
//     this._bubbles = this._bubbleGeometry(range?.from, range?.to);
//   }
//
//   paneViews() {
//     return this._views;
//   }
//
//   bubbleForId(id) {
//     return this._bubbles.find((bubble) => bubble.id === id) || null;
//   }
//
//   bubbleAtChartTime(time) {
//     const chartTime = Number(time);
//     if (!Number.isFinite(chartTime)) return null;
//     return this._bubbles.find((bubble) => toChartTime(bubble.t) === chartTime) || null;
//   }
//
//   hitTest(x, y) {
//     for (let index = this._bubbles.length - 1; index >= 0; index -= 1) {
//       const bubble = this._bubbles[index];
//       if (Math.hypot(x - bubble.cx, y - bubble.cy) <= bubble.r + 3) {
//         return {
//           distance: 0,
//           hitTestPriority: 2,
//           itemType: 'marker',
//           externalId: bubble.id,
//           zOrder: 'top',
//           cursorStyle: 'pointer',
//         };
//       }
//     }
//     return null;
//   }
//
//   autoscaleInfo(startTimePoint, endTimePoint) {
//     if (!this._series || !this._chart) return null;
//     const visible = this._bubbleGeometry(startTimePoint, endTimePoint);
//     if (!visible.length) return null;
//     let min = Infinity;
//     let max = -Infinity;
//     for (const bubble of visible) {
//       const top = this._series.coordinateToPrice?.(bubble.cy - bubble.r);
//       const bottom = this._series.coordinateToPrice?.(bubble.cy + bubble.r);
//       for (const price of [bubble.strike, top, bottom]) {
//         if (!Number.isFinite(price)) continue;
//         min = Math.min(min, price);
//         max = Math.max(max, price);
//       }
//     }
//     return Number.isFinite(min) && Number.isFinite(max)
//       ? { priceRange: { minValue: min, maxValue: max } }
//       : null;
//   }
//
//   _bubbleGeometry(startTimePoint, endTimePoint) {
//     const { dots, flowSnaps = [], minVolume = 0 } = this._data;
//     if (!dots || !this._chart || !this._series) return [];
//     const timeScale = this._chart.timeScale();
//     // `timeScale.width()` reports 0 in the bundled build while the pane is a
//     // real 446px, and 0 read as a measurement culled every bubble right of the
//     // axis origin. Ask the pane, and treat a non-positive width as "not
//     // measurable yet" rather than as an empty pane.
//     const paneWidth = this._chart.paneSize?.()?.width || timeScale.width?.();
//     const rightEdge = Number.isFinite(paneWidth) && paneWidth > 0 ? paneWidth : Infinity;
//     const raw = flowSnaps.map((snap) => {
//       const time = toChartTime(snap.t);
//       const logical = timeScale.timeToIndex?.(time, false);
//       const cx = timeScale.timeToCoordinate(time);
//       if (!Number.isFinite(cx)) return null;
//       if (cx < 0 || cx > rightEdge) return null;
//       if (
//         Number.isFinite(startTimePoint)
//         && Number.isFinite(endTimePoint)
//         && Number.isFinite(logical)
//         && (logical < startTimePoint || logical > endTimePoint)
//       ) {
//         return null;
//       }
//       const flow = aggregateFlowCells(snap.byStrike, snap.spot, STRIKE_WINDOW_PTS);
//       return {
//         ...flow,
//         t: snap.t,
//         cx,
//         recoveryGap: snap.recoveryGap,
//         gapMinutes: snap.gapMinutes,
//       };
//     }).filter((flow) => flow && Number.isFinite(flow.avgStrike) && flow.dVol > 0);
//     return selectVolumeDots(raw, minVolume).map((bubble) => {
//       const cy = this._series.priceToCoordinate(bubble.avgStrike);
//       return {
//         ...bubble,
//         id: `bookmap-bubble:${bubble.t}`,
//         strike: bubble.avgStrike,
//         cy,
//       };
//     }).filter((bubble) => Number.isFinite(bubble.cx) && Number.isFinite(bubble.cy));
//   }
//
//   _drawHeat(target) {
//     const {
//       snaps = [],
//       rows = [],
//       layer = 'gamma',
//       palette = 'bookmap',
//     } = this._data;
//
//     if (
//       !this._chart ||
//       !this._series ||
//       snaps.length < 2 ||
//       rows.length < 2
//     ) {
//       return;
//     }
//
//     const xs = snaps
//       .map((snap) => ({
//         snap,
//         x: this._chart
//           .timeScale()
//           .timeToCoordinate(toChartTime(snap.t)),
//       }))
//       .filter((entry) => Number.isFinite(entry.x));
//
//     const ys = rows
//       .slice()
//       .reverse()
//       .map((strike) => ({
//         strike,
//         y: this._series.priceToCoordinate(strike),
//       }))
//       .filter((entry) => Number.isFinite(entry.y));
//
//     if (xs.length < 2 || ys.length < 2) {
//       return;
//     }
//
//     target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
//       for (let xi = 0; xi < xs.length; xi += 1) {
//         const x = xs[xi];
//         const prev = xs[xi - 1];
//         const next = xs[xi + 1];
//
//         const minuteAgo = this._chart
//           .timeScale()
//           .timeToCoordinate(
//             toChartTime(x.snap.t) - 60
//           );
//
//         const neighborLeft = prev
//           ? prev.x
//           : next
//             ? x.x - (next.x - x.x)
//             : x.x - 8;
//
//         const rawLeft = clamp(
//           Number.isFinite(minuteAgo)
//             ? Math.max(neighborLeft, minuteAgo)
//             : neighborLeft,
//           0,
//           mediaSize.width,
//         );
//
//         const rawRight = clamp(
//           x.x,
//           0,
//           mediaSize.width,
//         );
//
//         /*
//          * Snap both boundaries to the nearest pixel.
//          *
//          * Do NOT use floor(left) / ceil(right).
//          * That causes adjacent transparent cells to overlap
//          * by roughly one pixel and creates visible vertical
//          * intensity stripes.
//          */
//         const left = Math.max(
//           0,
//           Math.round(rawLeft),
//         );
//
//         const right = Math.min(
//           mediaSize.width,
//           Math.round(rawRight),
//         );
//
//         if (right <= left) {
//           continue;
//         }
//
//         for (let yi = 0; yi < ys.length; yi += 1) {
//           const row = ys[yi];
//           const prevRow = ys[yi - 1];
//           const nextRow = ys[yi + 1];
//
//           const prevGap = prevRow
//             ? Math.abs(row.y - prevRow.y)
//             : Infinity;
//
//           const nextGap = nextRow
//             ? Math.abs(nextRow.y - row.y)
//             : Infinity;
//
//           const nearestGap = Math.min(
//             prevGap,
//             nextGap,
//           );
//
//           const rowSpacing = Number.isFinite(nearestGap)
//             ? nearestGap
//             : 8;
//
//           const bandHeight = clamp(
//             rowSpacing * HEAT_ROW_FILL_RATIO,
//             HEAT_ROW_MIN_HEIGHT,
//             HEAT_ROW_MAX_HEIGHT,
//           );
//
//           const top = clamp(
//             row.y - bandHeight / 2,
//             0,
//             mediaSize.height,
//           );
//
//           const bottom = clamp(
//             row.y + bandHeight / 2,
//             0,
//             mediaSize.height,
//           );
//
//           const value = cellValueAt(
//             x.snap,
//             row.strike,
//             layer,
//           );
//
//           if (
//             !value ||
//             bottom <= top
//           ) {
//             continue;
//           }
//
//           ctx.fillStyle = bookmapHeatCssColor(
//             bucketNormalizedValue(
//               value,
//               layer,
//             ),
//             palette,
//             layer,
//           );
//
//           ctx.fillRect(
//             left,
//             top,
//             right - left,
//             bottom - top,
//           );
//         }
//       }
//     });
//   }
//
//   _drawBubbles(target) {
//     const bubbles = this._bubbles;
//     if (!bubbles.length) return;
//     target.useMediaCoordinateSpace(({ context: ctx }) => {
//       for (const bubble of bubbles) {
//         const encoding = bubbleCanvasEncoding(bubble);
//         const outline = bubbleOutlineEncoding(bubble);
//         ctx.save();
//         if (encoding.kind === 'estimated') {
//           ctx.beginPath();
//           ctx.arc(bubble.cx, bubble.cy, bubble.r, 0, Math.PI * 2);
//           ctx.fillStyle = encoding.ambiguousStyle.fillStyle;
//           ctx.fill();
//           ctx.save();
//           ctx.beginPath();
//           ctx.arc(bubble.cx, bubble.cy, bubble.r, 0, Math.PI * 2);
//           ctx.clip();
//           ctx.strokeStyle = encoding.ambiguousStyle.hatchStyle;
//           ctx.lineWidth = encoding.ambiguousStyle.hatchLineWidth;
//           for (
//             let offset = -bubble.r * 2;
//             offset <= bubble.r * 2;
//             offset += encoding.ambiguousStyle.hatchGap
//           ) {
//             ctx.beginPath();
//             ctx.moveTo(bubble.cx - bubble.r + offset, bubble.cy - bubble.r);
//             ctx.lineTo(bubble.cx + bubble.r + offset, bubble.cy + bubble.r);
//             ctx.stroke();
//           }
//           ctx.restore();
//         } else {
//           ctx.beginPath();
//           ctx.arc(bubble.cx, bubble.cy, bubble.r, 0, Math.PI * 2);
//           ctx.fillStyle = encoding.kind === 'neutral'
//             ? 'rgba(140,148,160,0.8)'
//             : encoding.baseTone === 'bullish'
//               ? 'rgb(0,191,58)'
//               : 'rgb(208,0,0)';
//           ctx.fill();
//           for (const segment of encoding.geometry || []) {
//             if (segment.counterSweepDeg <= 0) continue;
//             const start = (segment.counterStartDeg * Math.PI) / 180;
//             const end = ((segment.counterStartDeg + segment.counterSweepDeg) * Math.PI) / 180;
//             ctx.beginPath();
//             ctx.moveTo(bubble.cx, bubble.cy);
//             ctx.arc(bubble.cx, bubble.cy, Math.max(1.5, bubble.r * 0.78), start, end);
//             ctx.closePath();
//             ctx.fillStyle = 'rgba(3,7,18,0.58)';
//             ctx.fill();
//           }
//         }
//         ctx.beginPath();
//         ctx.arc(bubble.cx, bubble.cy, bubble.r, 0, Math.PI * 2);
//         ctx.strokeStyle = 'rgba(5,7,10,0.7)';
//         ctx.lineWidth = 1.1;
//         ctx.setLineDash([]);
//         ctx.stroke();
//         ctx.beginPath();
//         ctx.arc(bubble.cx, bubble.cy, Math.max(0, bubble.r - 0.4), 0, Math.PI * 2);
//         ctx.strokeStyle = outline.primaryStroke;
//         ctx.lineWidth = 1.7;
//         ctx.stroke();
//         if (outline.recoveryGlowStroke) {
//           ctx.beginPath();
//           ctx.arc(bubble.cx, bubble.cy, bubble.r + 2.5, 0, Math.PI * 2);
//           ctx.strokeStyle = outline.recoveryGlowStroke;
//           ctx.lineWidth = 3;
//           ctx.stroke();
//         }
//         if (outline.climaxStroke) {
//           ctx.beginPath();
//           ctx.arc(bubble.cx, bubble.cy, bubble.r + 3.5, 0, Math.PI * 2);
//           ctx.strokeStyle = outline.climaxStroke;
//           ctx.lineWidth = 2;
//           ctx.stroke();
//         }
//         ctx.restore();
//       }
//     });
//   }
// }
